import React from "react"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Application } from "@/contexts/ApplicationContext"

interface PersonalInformationSectionProps {
  application: Application
  setApplication: (app: Application) => void
  dateInputValue: string
  setDateInputValue: (value: string) => void
}

const PersonalInformationSection: React.FC<PersonalInformationSectionProps> = ({
  application,
  setApplication,
  dateInputValue,
  setDateInputValue,
}) => {
  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setDateInputValue(value)

    // Update the application context with the new date
    if (value) {
      setApplication({
        ...application,
        basicDetails: {
          ...application.basicDetails,
          dateOfBirth: value,
        },
      })
    }
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "") // Remove non-digits
    if (value.length <= 15) {
      // Max 15 digits for international numbers
      setApplication({
        ...application,
        basicDetails: {
          ...application.basicDetails,
          phoneNumber: value,
        },
      })
    }
  }

  return (
    <div className="mb-6 space-y-4">
      <div className="flex items-center px-6 pt-6">
        <h2 className="text-lg font-bold text-jungle">Personal Information</h2>
      </div>
      <Separator className="mt-2" />

      <div className="bg-transparent p-6">
        <div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="firstName" className="text-sm md:text-base">
              First Name
            </Label>
            <Input
              id="firstName"
              value={application.basicDetails.firstName}
              onChange={(e) =>
                setApplication({
                  ...application,
                  basicDetails: {
                    ...application.basicDetails,
                    firstName: e.target.value,
                  },
                })
              }
              placeholder="Enter your first name"
              className="w-full bg-white"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName" className="text-sm md:text-base">
              Last Name
            </Label>
            <Input
              id="lastName"
              value={application.basicDetails.lastName}
              onChange={(e) =>
                setApplication({
                  ...application,
                  basicDetails: {
                    ...application.basicDetails,
                    lastName: e.target.value,
                  },
                })
              }
              placeholder="Enter your last name"
              className="w-full bg-white"
            />
          </div>
        </div>

        <div className="mb-5 space-y-2">
          <Label htmlFor="dateOfBirth" className="text-sm md:text-base">
            Date of Birth
          </Label>
          <Input
            id="dateOfBirth"
            type="date"
            value={dateInputValue}
            onChange={handleDateInputChange}
            placeholder="Select your date of birth"
            className="w-full bg-white"
          />
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm md:text-base">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              value={application.basicDetails.email}
              onChange={(e) =>
                setApplication({
                  ...application,
                  basicDetails: {
                    ...application.basicDetails,
                    email: e.target.value.trim().toLowerCase(),
                  },
                })
              }
              placeholder="Enter your email address"
              className="w-full bg-white"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phoneNumber" className="text-sm md:text-base">
              Phone Number
            </Label>
            <Input
              id="phoneNumber"
              type="tel"
              value={application.basicDetails.phoneNumber}
              onChange={handlePhoneChange}
              placeholder="Enter your phone number"
              className="w-full bg-white"
              minLength={10}
              maxLength={15}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default PersonalInformationSection
